import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ShimmerButton from '../components/magicui/shimmer-button';
import { Helmet } from 'react-helmet';

const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
    <svg
        width={16}
        height={16}
        className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="#45B8A9"
        strokeWidth={3}
        viewBox="0 0 24 24"
    >
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const CheckIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.15,
            duration: 0.5,
            ease: [0.25, 0.46, 0.45, 0.94],
        },
    }),
};

const featuredCardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            delay: 0.3,
            duration: 0.6,
            ease: [0.25, 0.46, 0.45, 0.94],
        },
    },
};

export const Previsionnel = () => {
    const [isOpenLeft, setIsOpenLeft] = useState(false);
    const [isOpenRight, setIsOpenRight] = useState(false);
    const navigate = useNavigate();

    return (
        <>
        <Helmet>
            <title>Budget prévisionnel personnalisé pour votre entreprise | FB Conseils</title>
            <meta name="description" content="Obtenez un budget prévisionnel complet et personnalisé pour votre création d'entreprise. Rendez-vous conseil ou formule complète avec un expert FB Conseils." />
            <link rel="canonical" href="https://maformejuridique.fr/previsionnel" />
            <meta property="og:title" content="Budget prévisionnel - FB Conseils" />
            <meta property="og:description" content="Budget prévisionnel personnalisé et rendez-vous conseil pour votre projet de création d'entreprise." />
            <meta property="og:url" content="https://maformejuridique.fr/previsionnel" />
        </Helmet>
        <div className='min-h-screen pt-6 sm:pt-12 md:pt-24' style={{ background: 'linear-gradient(180deg, #f8fffe 0%, #eef7f5 40%, #f0f9f7 100%)' }}>
            <div className="max-w-6xl mx-auto px-4 flex flex-col gap-6">

                {/* Section Questions/R??ponses */}
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Premi??re carte */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
                        style={{ borderLeft: '3px solid #45B8A9' }}
                    >
                        <button
                            onClick={() => setIsOpenLeft(!isOpenLeft)}
                            className="w-full flex items-center justify-between group"
                        >
                            <h2 className="text-lg font-semibold group-hover:opacity-80 transition-colors text-left pr-4" style={{ color: '#003942' }}>
                                {"Pourquoi avoir besoin d\u2019un budget pr\u00E9visionnel\u00A0?"}
                            </h2>
                            <ChevronIcon isOpen={isOpenLeft} />
                        </button>

                        <AnimatePresence>
                            {isOpenLeft && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                >
                                    <div className="mt-6 space-y-4 text-gray-600">
                                        <p className="flex gap-3 items-start">
                                            <span style={{ color: '#45B8A9' }} className="text-sm mt-0.5">{"\u25CF"}</span>
                                            <span>{"Un budget pr\u00E9visionnel est conseill\u00E9 lors d\u2019une cr\u00E9ation d\u2019entreprise afin d\u2019obtenir"}
                                            <strong style={{ color: '#003942' }}>{" une pr\u00E9vision des donn\u00E9es chiffr\u00E9es"}</strong>{" pour les premi\u00E8res ann\u00E9es d\u2019activit\u00E9."}</span>
                                        </p>
                                        <p className="flex gap-3 items-start">
                                            <span style={{ color: '#45B8A9' }} className="text-sm mt-0.5">{"\u25CF"}</span>
                                            <span>{"Il est aussi demand\u00E9 par certains organismes pour obtenir des financements (mat\u00E9riel professionnel, informatique, stock de d\u00E9part, tr\u00E9sorerie...)."}</span>
                                        </p>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    {/* Deuxi??me carte */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
                        style={{ borderLeft: '3px solid #45B8A9' }}
                    >
                        <button
                            onClick={() => setIsOpenRight(!isOpenRight)}
                            className="w-full flex items-center justify-between group"
                        >
                            <h2 className="text-lg font-semibold group-hover:opacity-80 transition-colors text-left pr-4" style={{ color: '#003942' }}>
                                {"Que contient votre budget pr\u00E9visionnel\u00A0?"}
                            </h2>
                            <ChevronIcon isOpen={isOpenRight} />
                        </button>

                        <AnimatePresence>
                            {isOpenRight && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                >
                                    <div className="mt-6 space-y-4 text-gray-600">
                                        <div className="flex gap-3 items-start">
                                            <span style={{ color: '#45B8A9' }} className="text-sm mt-0.5">{"\u25CF"}</span>
                                            <div>
                                                <strong style={{ color: '#003942' }}>{"Une pr\u00E9sentation d\u00E9taill\u00E9e de votre projet"}</strong>
                                                <p className="mt-1">{"Reprenant tous les \u00E9l\u00E9ments cl\u00E9s\u00A0: activit\u00E9, capital, chiffre d\u2019affaires, investissements, etc."}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3 items-start">
                                            <span style={{ color: '#45B8A9' }} className="text-sm mt-0.5">{"\u25CF"}</span>
                                            <div>
                                                <strong style={{ color: '#003942' }}>{"Un tableau de financement"}</strong>
                                                <p className="mt-1">{"D\u00E9taillant les besoins de tr\u00E9sorerie et les ressources au lancement."}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3 items-start">
                                            <span style={{ color: '#45B8A9' }} className="text-sm mt-0.5">{"\u25CF"}</span>
                                            <div>
                                                <strong style={{ color: '#003942' }}>{"Un compte de r\u00E9sultat \u00E0 3 ans"}</strong>
                                                <p className="mt-1">{"R\u00E9capitulatif des d\u00E9penses, recettes et r\u00E9sultats pr\u00E9visionnels."}</p>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Section Titre Pricing */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mt-12 text-center"
                >
                    <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#003942' }}>
                        {"Comment obtenir mon budget pr\u00E9visionnel\u00A0?"}
                    </h2>
                    <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                        {"Nous vous proposons trois formules adapt\u00E9es \u00E0 vos besoins"}
                    </p>
                    <div className="w-16 h-1 mx-auto mt-6 rounded-full" style={{ backgroundColor: '#45B8A9' }} />
                </motion.div>

                {/* Pricing Cards */}
                <div className="mt-10 mb-16 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-5 items-start">

                    {/* Tier 1 - Budget instantan?? */}
                    <motion.div
                        custom={0}
                        initial="hidden"
                        animate="visible"
                        variants={cardVariants}
                        whileHover={{ y: -6, transition: { duration: 0.25 } }}
                        className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
                    >
                        <div className="h-1.5 w-full" style={{ backgroundColor: '#45B8A9' }} />

                        <div className="p-7 flex flex-col" style={{ minHeight: '520px' }}>
                            <div className="mb-6">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(69, 184, 169, 0.12)' }}>
                                    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                                        <path d="M14 2v6h6" />
                                        <path d="M16 13H8" />
                                        <path d="M16 17H8" />
                                        <path d="M10 9H8" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold" style={{ color: '#003942' }}>
                                    {"Budget Pr\u00E9visionnel Instantan\u00E9"}
                                </h3>
                                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                                    {"Issu directement de votre simulation en ligne"}
                                </p>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-bold" style={{ color: '#003942' }}>{"59\u20AC"}</span>
                                    <span className="text-gray-400 text-sm font-medium">HT</span>
                                </div>
                            </div>

                            <div className="space-y-3.5 mb-8 flex-grow">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Forme juridique la plus adapt\u00E9e \u00E0 votre projet"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Budget d\u00E9taill\u00E9\u00A0: activit\u00E9, CA, v\u00E9hicule, r\u00E9mun\u00E9ration, fiscalit\u00E9"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Envoy\u00E9 au format PDF par email"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Mises \u00E0 jour fiscales incluses"}</span>
                                </div>
                            </div>

                            <div className="flex justify-center mt-auto">
                                <ShimmerButton
                                    onClick={() => navigate('/Form')}
                                    background="#003942"
                                    shimmerColor="#45B8A9"
                                    className="w-full text-white py-3.5 px-6 rounded-xl font-semibold text-base"
                                    size="small"
                                >
                                    {"D\u00E9marrer ma simulation"}
                                </ShimmerButton>
                            </div>
                        </div>
                    </motion.div>

                    {/* Tier 2 - RDV Conseil */}
                    <motion.div
                        custom={1}
                        initial="hidden"
                        animate="visible"
                        variants={cardVariants}
                        whileHover={{ y: -6, transition: { duration: 0.25 } }}
                        className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
                    >
                        <div className="h-1.5 w-full" style={{ backgroundColor: '#45B8A9' }} />

                        <div className="p-7 flex flex-col" style={{ minHeight: '520px' }}>
                            <div className="mb-6">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(69, 184, 169, 0.12)' }}>
                                    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M17 18a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2" />
                                        <rect width="18" height="18" x="3" y="4" rx="2" />
                                        <circle cx="12" cy="10" r="2" />
                                        <line x1="8" x2="8" y1="2" y2="4" />
                                        <line x1="16" x2="16" y1="2" y2="4" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold" style={{ color: '#003942' }}>
                                    {"Rendez-vous Conseil"}
                                </h3>
                                <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                                    {"\u00C9changez avec un expert en cr\u00E9ation d\u2019entreprise"}
                                </p>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-bold" style={{ color: '#003942' }}>{"99\u20AC"}</span>
                                    <span className="text-gray-400 text-sm font-medium">HT</span>
                                </div>
                            </div>

                            <div className="space-y-3.5 mb-8 flex-grow">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Consultation individuelle avec un professionnel"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Posez toutes vos questions sur votre projet"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Comprenez pourquoi une forme juridique est plus adapt\u00E9e"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-gray-600 text-sm leading-relaxed">{"Conseils personnalis\u00E9s pour votre situation"}</span>
                                </div>
                            </div>

                            <div className="flex justify-center mt-auto">
                                <ShimmerButton
                                    onClick={() => window.open('https://calendly.com/fbsimulateurs/55min', '_blank')}
                                    background="#003942"
                                    shimmerColor="#45B8A9"
                                    className="w-full text-white py-3.5 px-6 rounded-xl font-semibold text-base"
                                    size="small"
                                >
                                    Prendre RDV
                                </ShimmerButton>
                            </div>
                        </div>
                    </motion.div>

                    {/* Tier 3 - Formule Compl??te (RECOMMENDED) */}
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={featuredCardVariants}
                        whileHover={{ y: -8, transition: { duration: 0.25 } }}
                        className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300"
                        style={{ background: '#003942' }}
                    >
                        {/* Recommended badge */}
                        <div className="absolute top-5 right-5 z-10">
                            <span
                                className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full"
                                style={{ backgroundColor: '#45B8A9', color: '#003942' }}
                            >
                                {"Recommand\u00E9"}
                            </span>
                        </div>

                        {/* Subtle glow effect */}
                        <div
                            className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 blur-3xl pointer-events-none"
                            style={{ backgroundColor: '#45B8A9' }}
                        />

                        <div className="relative p-7 flex flex-col" style={{ minHeight: '520px' }}>
                            <div className="mb-6">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(69, 184, 169, 0.2)' }}>
                                    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-white">
                                    {"Formule Compl\u00E8te"}
                                </h3>
                                <p className="text-sm mt-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {"Budget pr\u00E9visionnel personnalis\u00E9 + Rendez-vous conseil"}
                                </p>
                            </div>

                            <div className="mb-6">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-bold" style={{ color: '#45B8A9' }}>{"249\u20AC"}</span>
                                    <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>HT</span>
                                </div>
                            </div>

                            <div className="space-y-3.5 mb-8 flex-grow">
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{"Budget pr\u00E9visionnel 100% personnalis\u00E9"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{"RDV avec un expert en cr\u00E9ation d\u2019entreprise"}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{"Id\u00E9al si\u00A0: ARE, double activit\u00E9, retrait\u00E9..."}</span>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 flex-shrink-0"><CheckIcon /></div>
                                    <span className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>{"Accompagnement complet et sur-mesure"}</span>
                                </div>
                            </div>

                            <div className="flex justify-center mt-auto">
                                <ShimmerButton
                                    onClick={() => window.open('https://calendly.com/fbsimulateurs/rendez-vous-conseils-budget-previsionnel', '_blank')}
                                    background="#45B8A9"
                                    shimmerColor="#ffffff"
                                    className="w-full py-3.5 px-6 rounded-xl font-semibold text-base"
                                    size="small"
                                >
                                    <span style={{ color: '#003942', fontFamily: "'Outfit', sans-serif" }}>{"Choisir cette formule"}</span>
                                </ShimmerButton>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Disclaimer */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                    className="mb-16 -mt-6"
                >
                    <div className="flex items-start gap-3 bg-white/80 backdrop-blur-sm rounded-xl p-5 border max-w-3xl mx-auto" style={{ borderColor: 'rgba(69, 184, 169, 0.15)' }}>
                        <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#45B8A9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 16v-4" />
                            <path d="M12 8h.01" />
                        </svg>
                        <p className="text-sm text-gray-500 leading-relaxed">
                            <strong className="text-gray-700">{"A savoir\u00A0:"}</strong>{" notre budget pr\u00E9visionnel est complet. Il tient compte de l\u2019activit\u00E9 choisie, du niveau de chiffre d\u2019affaires, de l\u2019utilisation d\u2019un v\u00E9hicule pro ou perso, du niveau de r\u00E9mun\u00E9ration, de l\u2019imp\u00F4t sur soci\u00E9t\u00E9 avec les mises \u00E0 jour fiscales."}
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
        </>
    );
};
